import { createGlobalState } from '@vueuse/core';
import { ref, nextTick } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { aiMessageVettingAgentsClient } from '~/services';
import type { AiMessageVettingAgentPayload } from '~/services/aiMessageVettingAgents/aiMessageVettingAgentsClient.types';
import { catchRequestError } from '~/utils/request';

export const defaultAiMessageVettingAgentFormData = (): AiMessageVettingAgentPayload => ({
    name: '',
    description: '',
    instructions: '',
    validation: 'warn',
});

export const useAiMessageVettingAgentDetails = createGlobalState(() => {
    const toasted = useToasted();
    const teamId = Spark.state.currentTeam.id;

    const agent = ref<AiMessageVettingAgentPayload>(defaultAiMessageVettingAgentFormData());
    const loading = ref(true);

    let onMountedAgentCallback: CallableFunction = () => {};
    const onMountedAgent = (callback: CallableFunction) => {
        onMountedAgentCallback = callback;
    };

    const mount = async (agentId?: number) => {
        await nextTick();

        if (!agentId) {
            agent.value = defaultAiMessageVettingAgentFormData();
            loading.value = false;
            onMountedAgentCallback?.();
            return;
        }

        try {
            loading.value = true;
            const response = await aiMessageVettingAgentsClient.getAiMessageVettingAgent({ teamId }, agentId);
            agent.value = {
                name: response.data.name,
                description: response.data.description,
                instructions: response.data.instructions,
                validation: response.data.validation,
            };

            loading.value = false;
            onMountedAgentCallback?.();
        } catch (e) {
            toasted?.error(catchRequestError(e));
        }
    };

    const unmount = () => {
        agent.value = defaultAiMessageVettingAgentFormData();
        loading.value = false;
    };

    return {
        loading,
        agent,
        mount,
        unmount,
        onMountedAgent,
    };
});
